import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { styled, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { RichText, Text, useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import { UIButton } from 'components/shared/UIButton.styles';
import Image from 'next/image';
import React, { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { EMAIL_TEMPLATES } from 'src/constants';
import { setHcpRegistrationData } from 'src/features/hcpSlice';
import { hideLoader, showLoader } from 'src/features/loaderSlice';
import { callDapApiSendEmail, hcpRegistration } from 'src/services/HcpService';
import { RootState } from 'src/store/store';
import ConnectUsForm from './ConnectUsForm';
import { ConnectUsMergeTag } from './ConnectUsMergeTag';
import notificationIcon from './notification_icon.svg';
import { updateCRQueryParamList } from 'src/features/crQueryParamSlice';
import axios from 'axios';
import { DEFAULT_PRIMARY_FONT_FAMILY, DEFAULT_PRIMARY_SEMIBOLD_FONT } from 'theme/fontDefaults';

export const StConnectUsContainer = styled(Grid)(({ theme }) => ({
  background: theme.backgroundGradients?.backgroundGradientColor1 ?? '',
  color: theme.palette.white.main,
  [theme.breakpoints.down(500)]: {
    paddingInline: '2rem',
  },
}));

const CheckboxInput: React.FC<any> = React.forwardRef((props) => {
  const { theme, value, ...rest } = props;
  return (
    <Checkbox
      checked={!!value}
      {...rest}
      sx={{
        alignSelf: 'flex-start',
        padding: '2px 0',
        color: theme.palette.white.main,
        '.MuiButtonBase-root-MuiCheckbox-root': {
          color: `${theme.palette.white.main} !important`,
          backgroundColor: theme.palette.white.main,
        },

        '.MuiSvgIcon-root': {
          backgroundColor: theme.palette.white.main,
          borderRadius: '1px',
          fontSize: '1rem',
        },
      }}
    />
  );
});
CheckboxInput.displayName = 'CheckboxInput';

const ConnectUs = (props: any) => {
  const { sitecoreContext } = useSitecoreContext();
  const siteSettings: any = sitecoreContext?.siteSettings;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down(530));
  const dispatch = useDispatch();
  const hcpReduxData = useSelector((state: RootState) => state.hcpRegistrationData);
  const crQueryParams = useSelector((state: RootState) => state.crQueryParam.queryParamList);
  const [showThankYouPage, setShowThankYouPage] = useState<boolean>(hcpReduxData?.userRegistered);
  const {
    control,
    getValues,
    setError,
    handleSubmit,
    formState: { errors },
  } = useForm({ mode: 'all' });

  const sendHCPEmail = async (emailAddress: string) => {
    const mailIdList = siteSettings?.hCPMailingListId?.value || '';
    const emailType = EMAIL_TEMPLATES.HCP_REGISTRATION;
    const baseUrl = siteSettings?.baseUrl?.value || '';
    const payload = {
      email: emailAddress,
      mailIdList,
      emailType,
      baseUrl,
      siteSettingsId: sitecoreContext?.siteSettingsId as any,
      language: sitecoreContext?.language as any,
    };
    await callDapApiSendEmail(payload);
  };

  const handleSave = async () => {
    let paramList: any = [];

    if (Object.keys(crQueryParams).length > 0) {
      paramList = Object.entries(crQueryParams).map(([key, value]) => ({
        key,
        value,
      }));
    }
    let ipAddress = '';
    try {
      const { data: res } = await axios.get(`/api/getIp`);
      ipAddress = res?.ip || '';
    } catch (_e: any) {}
    dispatch(showLoader());
    const data = getValues();
    const payload = {
      ...data,
      ipAddress: ipAddress,
      registrationDateTime: new Date().toISOString(),
      privacyPolicyAccepted: data?.privacyPolicyAccepted ? 'true' : 'false',
      utmParameterList: paramList,
    };
    const response = await hcpRegistration(payload);
    if (response.data) {
      if (response?.data?.isEmailExist) {
        setError('emailAddress', {
          type: 'EMAIL_ALREADY_EXISTS',
          message: 'Email already exists',
        });
      } else {
        dispatch(setHcpRegistrationData(payload));
        dispatch(updateCRQueryParamList({}));
        await sendHCPEmail(data.emailAddress);
        setShowThankYouPage(true);
      }
      dispatch(hideLoader());
    }
  };

  return (
    <StConnectUsContainer>
      {showThankYouPage ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            paddingBlock: '6rem 8rem',
            gap: '1rem',
          }}
        >
          {/* TODO: Change to image
          <Image
            src={props?.fields?.thankYouIcon?.value?.src}
            width={props?.fields?.thankYouIcon?.value?.width}
            height={props?.fields?.thankYouIcon?.value?.height}
            layout='fill'
          /> */}
          <CheckCircleIcon sx={{ height: '94px', width: '94px', marginBottom: '2rem' }} />
          <Box
            sx={{
              fontFamily: `${
                siteSettings?.fontFamily?.fields?.primaryFontFamilyType?.value ||
                DEFAULT_PRIMARY_FONT_FAMILY
              } Regular, sans-serif`,
              fontWeight: 600,
              fontSize: '2rem',
              lineHeight: '36px',
              marginBottom: '2rem',
            }}
          >
            <ConnectUsMergeTag field={props?.fields?.thankYouTitle} />
          </Box>
          <Box
            sx={{
              fontFamily: `${
                siteSettings?.fontFamily?.fields?.primaryFontFamilyType?.value ||
                DEFAULT_PRIMARY_FONT_FAMILY
              } Regular, sans-serif`,
              fontWeight: 500,
              fontSize: '20px',
              lineHeight: '32px',
              textAlign: 'center',
            }}
          >
            <RichText field={props?.fields?.thankYouSubTitle} />
          </Box>
        </Box>
      ) : (
        <form onSubmit={handleSubmit(handleSave)}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              paddingBlock: '4rem',
              gap: '1rem',
            }}
          >
            <Box
              sx={{
                fontWeight: 700,
                fontSize: '2rem',
                textAlign: 'center',
              }}
            >
              <Text field={props?.fields?.title} />
            </Box>
            <Box
              sx={{
                fontFamily: `${
                  siteSettings?.fontFamily?.fields?.primaryFontSemiBoldType ||
                  DEFAULT_PRIMARY_SEMIBOLD_FONT
                }, sans-serif`,
                fontWeight: 500,
                fontSize: '18px',
                padding: 0,
                margin: 0,
                textAlign: 'center',
              }}
            >
              <RichText field={props?.fields?.subTitle} />
            </Box>
            {errors && Object.keys(errors).length > 0 && (
              <Box
                sx={{
                  width: isMobile ? '300px' : '450px',
                  alignSelf: 'center',
                }}
              >
                <Paper sx={{ p: 2, borderRadius: '8px', backgroundColor: '#E6F9F8' }}>
                  <Stack direction="row" alignItems="center" sx={{ marginLeft: '0.5rem' }}>
                    <Image
                      style={{ color: theme.palette.error.main }}
                      priority
                      src={notificationIcon}
                      alt="Notification icon"
                    />
                    <Typography
                      sx={{
                        color: theme.palette.error.main,
                        fontWeight: 400,
                        lineHeight: '1.25rem',
                        marginLeft: '1rem',
                      }}
                    >
                      {`${props?.fields?.genericError?.value}`}
                    </Typography>
                  </Stack>
                </Paper>
              </Box>
            )}
            {props?.fields?.formInput?.map((formFields: any, formFieldsIndex: number) => {
              if (formFields?.fields?.fieldSet && formFields?.fields?.fieldSet?.length > 0) {
                return (
                  <React.Fragment key={formFieldsIndex}>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        width: isMobile ? '300px' : '450px',
                      }}
                    >
                      <Box
                        sx={{
                          fontFamily: `${
                            siteSettings?.fontFamily?.fields?.primaryFontFamilyType?.value ||
                            DEFAULT_PRIMARY_FONT_FAMILY
                          } Regular, sans-serif`,
                          fontWeight: 400,
                          fontSize: '14px',
                          lineHeight: '14px',
                          color: `${theme.palette.white.main}`,
                        }}
                      >
                        <Text field={formFields?.fields?.label} />
                      </Box>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: isMobile ? 'column' : 'row',
                          width: isMobile ? '300px' : '450px',
                          gap: '0.5rem',
                        }}
                      >
                        {formFields?.fields?.fieldSet?.map(
                          (subFields: any, subFieldsIndex: number) => {
                            return (
                              <ConnectUsForm
                                key={`${formFieldsIndex}-${subFieldsIndex}`}
                                control={control}
                                errors={errors}
                                getValues={getValues}
                                type={
                                  subFields?.fields?.fieldType?.value ||
                                  subFields?.fields?.type?.value
                                }
                                formElements={subFields}
                                name={subFields?.fields?.key?.value}
                              />
                            );
                          }
                        )}
                      </Box>
                    </Box>
                  </React.Fragment>
                );
              } else {
                return (
                  <ConnectUsForm
                    key={`${formFieldsIndex}`}
                    control={control}
                    errors={errors}
                    getValues={getValues}
                    type={formFields?.fields?.fieldType?.value || formFields?.fields?.type?.value}
                    formElements={formFields}
                    name={formFields?.fields?.key?.value}
                  />
                );
              }
            })}
            <Box sx={{ alignSelf: 'center' }}>
              <Controller
                control={control}
                name="privacyPolicyAccepted"
                defaultValue={false}
                rules={{
                  required: {
                    value: true,
                    message: props?.fields?.consentRequiredValidationMessage?.value || '',
                  },
                }}
                render={({ field }) => (
                  <FormControl sx={{ width: isMobile ? '300px' : '450px', alignSelf: 'center' }}>
                    <FormControlLabel
                      sx={{
                        margin: 0,
                        gap: '0.5rem',
                        '.MuiFormControlLabel-label': {
                          fontFamily: `${
                            siteSettings?.fontFamily?.fields?.primaryFontFamilyType?.value ||
                            DEFAULT_PRIMARY_FONT_FAMILY
                          } Regular, sans-serif`,
                          fontWeight: 400,
                          fontSize: '14px',
                          lineHeight: '20px',
                          color: `${theme.palette.white.main}`,
                          alignItems: 'center',
                        },
                      }}
                      control={<CheckboxInput {...field} theme={theme} />}
                      label={
                        <RichText className="description" field={props?.fields?.privacyConsent} />
                      }
                    />
                    {errors['privacyPolicyAccepted'] !== undefined && (
                      <Box
                        sx={{
                          fontSize: '12px',
                          fontFamily: `${
                            siteSettings?.fontFamily?.fields?.primaryFontSemiBoldType ||
                            DEFAULT_PRIMARY_SEMIBOLD_FONT
                          }`,
                        }}
                      >
                        {(errors['privacyPolicyAccepted']?.message as string) ||
                          'Consent is required'}
                      </Box>
                    )}
                  </FormControl>
                )}
              />
            </Box>

            <UIButton
              variant="outlined"
              sx={{
                padding: '5px',
                width: isMobile ? '300px' : 'auto',
              }}
              type="submit"
            >
              <Text field={props?.fields?.buttonText} />
            </UIButton>
          </Box>
        </form>
      )}
    </StConnectUsContainer>
  );
};

export default ConnectUs;
